import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import { useAppStore } from '@/hooks/useAppStore';
import { applyMembershipSnapshot } from '@/hooks/useAppStore';
import { useAppConfig } from '@/hooks/useAppConfig';
import { getMe } from '@/api/user';
import {
  createOrder,
  cancelAppleOrder,
  getOrder,
  getPackages,
  getSubscription,
  getSubscriptionManagement,
  recoverAppleIAP,
  verifyAppleIAP,
} from '@/api/pay';
import type { CreateOrderResponse } from '@/api/pay';
import { track } from '@/utils/tracker';
import type { VipSubscription } from '@/api/pay';
import AgreementSheet from '@/components/feature/AgreementSheet';
import { useLocalize } from '@/utils/localize';
import { randomUUID } from '@/utils/uuid';
import {
  bindPendingPaymentContextToUser,
  clearPendingApplePaymentContext,
  clearPendingPaymentContext,
  loadPendingApplePaymentContext,
  savePendingApplePaymentContext,
} from '@/utils/paymentContext';
import {
  getH5PaymentRecoveryDecision,
  pollH5PaymentOrder,
  shouldBlockNewH5Checkout,
  shouldClearPendingPaymentContext,
} from './h5PaymentRecovery';
import {
  getMembershipDisplayPrice,
  shouldShowBackendPriceDetails,
} from './membershipPrice';
import {
  shouldShowAutoRenewStatus,
  validateStoreProductContract,
} from './paymentContract';
import { restoreApplePurchases } from './applePurchaseRestore';
import {
  buildDisplayPackages,
  type DisplayPackage,
} from './membershipPackages';

const isIOS = (): boolean => {
  return Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios'
}

const currentPayPlatform = (): 'ios' | 'android' | 'web' => {
  if (!Capacitor.isNativePlatform()) return 'web'
  return Capacitor.getPlatform() === 'ios' ? 'ios' : 'android'
}

function normalizeStorefrontCountry(countryCode?: string | null): string {
  return (countryCode || '').trim().toUpperCase()
}

function getPriceUnit(id: string, t: (key: string) => string): string {
  if (id === 'single') return '';
  if (id === 'yearly') return t('member_perYear');
  if (id === 'quarterly') return t('member_perQuarter');
  return t('member_perMonth');
}

function getDailyPrice(pkg: DisplayPackage, t: (key: string, opts?: Record<string, unknown>) => string): string | null {
  if (!shouldShowBackendPriceDetails(pkg.store_price_required)) return null;
  if (!pkg.valid_days || pkg.valid_days <= 1) return null;
  return t('member_onlyPerDay', { currency: pkg.currency_symbol ?? '¥', price: (Math.round(pkg.price * 100 / pkg.valid_days) / 100).toFixed(2) });
}

function getDisplayPrice(pkg?: DisplayPackage): string {
  if (!pkg) return ''
  return getMembershipDisplayPrice({
    storePriceRequired: pkg.store_price_required,
    storeProduct: pkg.store_product,
    backendPrice: pkg.price,
    backendCurrencySymbol: pkg.currency_symbol,
  })
}

function splitDisplayPrice(displayPrice: string): {
  prefix: string
  integer: string
  decimal: string
} {
  const match = displayPrice.trim().match(/^(.*?)(\d[\d,\s]*)([.,]\d{1,2})?$/u)
  if (!match) return { prefix: '', integer: displayPrice, decimal: '' }
  return {
    prefix: match[1],
    integer: match[2],
    decimal: match[3] ?? '',
  }
}

const pollOrder = (orderId: string) =>
  pollH5PaymentOrder({ orderId, getOrder })

function PkgCard({
  pkg,
  selected,
  priceLoading,
  onSelect,
}: {
  pkg: DisplayPackage;
  selected: boolean;
  priceLoading: boolean;
  onSelect: () => void;
}) {
  const localize = useLocalize();
  const { t } = useTranslation();
  const unit = getPriceUnit(pkg.id, t);
  const daily = getDailyPrice(pkg, t);
  const saved = pkg.store_price_required
    ? 0
    : Math.round((pkg.original_price - pkg.price) * 100) / 100;
  const price = splitDisplayPrice(getDisplayPrice(pkg));

  return (
    <div
      onClick={onSelect}
      data-testid="pkg-card"
      data-package-id={pkg.id}
      data-selected={selected ? 'true' : 'false'}
      className="flex-shrink-0 w-[136px] min-h-[150px] rounded-2xl p-3 cursor-pointer transition-all relative flex flex-col"
      style={
        selected
          ? { background: pkg.gradient, boxShadow: `0 6px 20px ${pkg.accent_color}45` }
          : { background: 'white', border: `1.5px solid ${pkg.accent_color}25` }
      }
    >
      {pkg.tag && (
        <div
          className="absolute -top-2.5 left-1/2 -translate-x-1/2 px-2 py-0.5 rounded-full text-[9px] font-bold whitespace-nowrap"
          style={
            selected
              ? { background: 'rgba(255,255,255,0.9)', color: pkg.accent_color }
              : { background: pkg.gradient, color: 'white' }
          }
        >
          {localize(pkg, 'tag')}
        </div>
      )}

      <p className={`text-[11px] font-semibold mb-2 ${selected ? 'text-white/80' : 'text-gray-400'}`}>{localize(pkg, 'name')}</p>

      {priceLoading ? (
        <div
          className={`h-8 w-20 rounded-lg animate-pulse mb-1 ${selected ? 'bg-white/25' : 'bg-gray-100'}`}
          data-testid="store-price-skeleton"
        />
      ) : (
        <div className="flex items-baseline gap-0 mb-1 overflow-hidden whitespace-nowrap" data-price-source={pkg.store_price_required ? 'storekit' : 'backend'}>
          <span className={`text-[14px] font-semibold leading-none ${selected ? 'text-white' : 'text-gray-800'}`}>
            {price.prefix}
          </span>
          <span className={`text-[28px] font-bold leading-none ${selected ? 'text-white' : 'text-gray-800'}`}>
            {price.integer}
          </span>
          <span className={`text-[18px] font-bold leading-none ${selected ? 'text-white' : 'text-gray-800'}`}>
            {price.decimal}
          </span>
          <span className={`text-[10px] leading-none ml-0.5 ${selected ? 'text-white/70' : 'text-gray-400'}`}>{unit}</span>
        </div>
      )}

      {shouldShowBackendPriceDetails(pkg.store_price_required) && (
        <p className={`text-[10px] line-through mb-1 ${selected ? 'text-white/50' : 'text-gray-300'}`}>
          {pkg.currency_symbol ?? '¥'}{pkg.original_price}
        </p>
      )}

      {daily && (
        <p className={`text-[9px] font-medium leading-tight ${selected ? 'text-white/80' : ''}`}
          style={selected ? {} : { color: pkg.accent_color }}>
          {daily}
        </p>
      )}

      {(saved > 0 || pkg.purchase_mode === 'consumable') && (
        <div className="mt-auto pt-2">
          <div
            className="text-center text-[10px] font-bold py-1 rounded-lg"
            style={
              selected
                ? { background: 'rgba(255,255,255,0.22)', color: 'white' }
                : { background: `${pkg.accent_color}12`, color: pkg.accent_color }
            }
          >
            {saved > 0
              ? t('member_save', { amount: saved, currency: pkg.currency_symbol ?? '¥' })
              : t('member_neverExpires')}
          </div>
        </div>
      )}
    </div>
  );
}

function PackageCardsSkeleton() {
  const { t } = useTranslation()

  return (
    <div role="status" aria-live="polite" data-testid="package-cards-skeleton">
      <div className="flex gap-3 overflow-hidden px-5 pb-3 pt-3">
        {[0, 1, 2].map((item) => (
          <div key={item} className="flex-shrink-0 w-[136px] min-h-[150px] rounded-2xl bg-white border border-gray-100 p-3 animate-pulse">
            <div className="h-3 w-16 rounded bg-gray-100 mb-5" />
            <div className="h-8 w-20 rounded bg-gray-100 mb-3" />
            <div className="h-3 w-12 rounded bg-gray-100 mt-auto" />
          </div>
        ))}
      </div>
      <p className="text-center text-xs text-gray-400 mt-1">{t('member_packagesLoading')}</p>
    </div>
  )
}

function PackageLoadError({
  message,
  accentColor,
  onRetry,
}: {
  message: string
  accentColor: string
  onRetry: () => void
}) {
  const { t } = useTranslation()

  return (
    <div className="mx-5 rounded-2xl bg-white px-4 py-4 text-center" role="alert" data-testid="package-load-error">
      <p className="text-xs text-gray-500 mb-3">{message}</p>
      <button
        type="button"
        className="h-9 px-4 rounded-xl text-xs font-semibold"
        style={{ color: accentColor, background: `${accentColor}0F`, border: `1px solid ${accentColor}30` }}
        onClick={onRetry}
      >
        {t('member_retry')}
      </button>
    </div>
  )
}

function RestorePurchasesButton({
  disabled,
  restoring,
  needsSync,
  accentColor,
  onRestore,
  onSync,
}: {
  disabled: boolean
  restoring: boolean
  needsSync: boolean
  accentColor: string
  onRestore: () => void
  onSync: () => void
}) {
  const { t } = useTranslation()

  return (
    <>
    <button
      type="button"
      data-testid="restore-purchases"
      aria-busy={restoring}
      onClick={onRestore}
      disabled={disabled}
      className="w-full h-12 rounded-2xl font-semibold text-sm cursor-pointer whitespace-nowrap disabled:opacity-60 active:scale-95 transition-transform"
      style={{
        color: accentColor,
        background: `${accentColor}0F`,
        border: `1px solid ${accentColor}38`,
      }}
    >
      <i className={`ri-refresh-line mr-1.5 ${restoring ? 'animate-spin' : ''}`} aria-hidden="true"></i>
      {restoring ? t('member_restoringPurchases') : t('member_restorePurchases')}
    </button>
    {needsSync && !restoring && (
      <div className="mt-2.5 rounded-xl px-3 py-2.5" style={{ background: `${accentColor}0A` }} data-testid="restore-sync-prompt">
        <p className="text-[11px] leading-relaxed text-gray-500 mb-2">{t('member_syncAppStoreHint')}</p>
        <button
          type="button"
          onClick={onSync}
          disabled={disabled}
          className="w-full h-10 rounded-xl text-xs font-semibold disabled:opacity-60"
          style={{ color: accentColor, border: `1px solid ${accentColor}38` }}
        >
          {t('member_syncAppStore')}
        </button>
      </div>
    )}
    </>
  )
}

type ModalType = 'memberAgreement' | 'autoRenewAgreement' | null;

export default function MembershipPage() {
  const navigate = useNavigate();
  const { user, authInitialized } = useAppStore();
  const { t, i18n } = useTranslation();
  const config = useAppConfig();
  const scrollRef = useRef<HTMLDivElement>(null);
  const applePurchaseInFlightRef = useRef(false);
  const localize = useLocalize();

  const [displayPackages, setDisplayPackages] = useState<DisplayPackage[]>([]);
  const [subscription, setSubscription] = useState<VipSubscription | null>(null);
  const [packagesLoading, setPackagesLoading] = useState(true);
  const [storeProductsLoading, setStoreProductsLoading] = useState(isIOS());
  const [packagesError, setPackagesError] = useState(false);
  const [storeProductsError, setStoreProductsError] = useState(false);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [purchasing, setPurchasing] = useState(false);
  const [restoringPurchases, setRestoringPurchases] = useState(false);
  const [restoreNeedsSync, setRestoreNeedsSync] = useState(false);
  const [toast, setToast] = useState<{ text: string; type: 'success' | 'error' } | null>(null);
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  const [paySuccessData, setPaySuccessData] = useState<{ packageName: string; pointsGifted: number } | null>(null);
  const [storefrontCountry, setStorefrontCountry] = useState('');

  const [selectedPkgId, setSelectedPkgId] = useState<string>('');

  const effectivePkgId = selectedPkgId;
  const currentPkg = displayPackages.find((p) => p.id === effectivePkgId) ?? displayPackages[0];
  const priceUnit = currentPkg ? getPriceUnit(currentPkg.id, t) : '';
  const accentColor = currentPkg?.accent_color ?? '#FF6B9D';
  const gradient = currentPkg?.gradient ?? 'linear-gradient(135deg, #FF6B9D, #A855F7)';

  useEffect(() => {
    track.pageView('membership');
  }, []);

  // Reset purchasing spinner when user becomes VIP (IAP success race condition)
  useEffect(() => {
    if (user?.isVip && purchasing) {
      setPurchasing(false)
    }
  }, [user?.isVip, purchasing])

  // 恢复上次中断的支付：Apple 交易只有服务端确认后才 finish。
  useEffect(() => {
    const pendingId = localStorage.getItem('pendingOrderId')
    const pendingPayChannel = localStorage.getItem('pendingPayChannel')
    if (purchasing) return
    let cancelled = false
    const recover = async () => {
      try {
        if (!pendingId) {
          return
        }
        const result = await pollOrder(pendingId)
        if (cancelled) return
        const decision = getH5PaymentRecoveryDecision(result)
        if (shouldClearPendingPaymentContext(result, pendingPayChannel)) {
          clearPendingPaymentContext()
        }
        if (decision.declareSuccess) {
          try {
            const [sub, me] = await Promise.all([getSubscription(), getMe()])
            applyMembershipSnapshot(sub, me.points)
            setSubscription(sub)
          } catch { /* best-effort */ }
          showToast(t('member_paySuccessMsg'), 'success')
          navigateAfterPay()
        } else if (result === 'fulfillment_repairing') {
          showToast(t('member_payRepairing'), 'error')
        } else if (result === 'payment_failed') {
          showToast(t('member_payIncomplete'), 'error')
        }
      } catch { /* ignore */ }
    }
    void recover()
    return () => { cancelled = true }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setPackagesLoading(true)
      setPackagesError(false)
      setStoreProductsError(false)
      setStoreProductsLoading(isIOS())
      try {
        const [pkgs, sub] = await Promise.all([getPackages(), getSubscription()]);
        if (cancelled) return

        const storePriceRequired = isIOS()
        const availablePackages = storePriceRequired
          ? pkgs
          : pkgs.filter((pkg) => !pkg.is_auto_renew)
        const initialPackages = buildDisplayPackages({
          packages: availablePackages,
          storePriceRequired,
        })
        setDisplayPackages(initialPackages)
        setSubscription(sub)
        setSelectedPkgId((previous) => {
          if (initialPackages.some((pkg) => pkg.id === previous)) return previous
          const activePkg = user?.isVip && sub?.package_id
            ? initialPackages.find((pkg) => pkg.id === sub.package_id)
            : undefined
          return (activePkg
            ?? initialPackages.find((pkg) => pkg.is_recommended)
            ?? initialPackages[0])?.id ?? ''
        })
        setPackagesLoading(false)

        if (!storePriceRequired) {
          setStoreProductsLoading(false)
          return
        }

        const bridge = window.__NATIVE_BRIDGE__
        if (!bridge?.getStoreProducts) {
          console.error('[membership] StoreKit product bridge unavailable')
          setStoreProductsError(true)
          setStoreProductsLoading(false)
          return
        }

        const productIds = availablePackages
          .map((pkg) => pkg.apple_product_id)
          .filter((productId): productId is string => Boolean(productId))
        try {
          const storefrontPromise = bridge.getStorefront
            ? bridge.getStorefront().catch((error) => {
              console.warn('[membership] Storefront metadata unavailable', error)
              return { countryCode: '' }
            })
            : Promise.resolve({ countryCode: '' })
          const [storefront, result] = await Promise.all([
            storefrontPromise,
            bridge.getStoreProducts(productIds),
          ])
          if (cancelled) return

          const storefrontCode = normalizeStorefrontCountry(storefront.countryCode)
          setStorefrontCountry(storefrontCode)
          setDisplayPackages(buildDisplayPackages({
            packages: availablePackages,
            storeProducts: result.products,
            storePriceRequired: true,
          }))
        } catch (error) {
          if (cancelled) return
          console.error('[membership] StoreKit product metadata unavailable', error)
          setStoreProductsError(true)
        } finally {
          if (!cancelled) setStoreProductsLoading(false)
        }
      } catch (error) {
        if (cancelled) return
        console.error('[membership] package metadata unavailable', error)
        setPackagesError(true)
        setStoreProductsLoading(false)
      } finally {
        if (!cancelled) setPackagesLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [i18n.language, loadAttempt, user?.isVip]);

  const showToast = (text: string, type: 'success' | 'error' = 'success') => {
    setToast({ text, type });
    setTimeout(() => setToast(null), 3000);
  };

  function navigateAfterPay() {
    const raw = sessionStorage.getItem('paySource');
    if (raw) {
      try {
        const { route, action, params } = JSON.parse(raw) as {
          route: string;
          action?: string;
          params?: { gen_id?: unknown };
        };
        const returnRoute = (
          (action === 'first_generation_success_offer' || action === 'generation_success_offer')
          && typeof params?.gen_id === 'string'
        )
          ? `/profile?tab=history&gen_id=${encodeURIComponent(params.gen_id)}`
          : route
        if (action === 'first_generation_success_offer' || action === 'generation_success_offer') {
          sessionStorage.removeItem('paySource')
        }
        navigate(returnRoute);
        return;
      } catch {
        sessionStorage.removeItem('paySource');
      }
    }
    navigate('/profile');
  }

  const getOfferContextId = (): string | undefined => {
    const raw = sessionStorage.getItem('paySource')
    if (!raw) return undefined
    try {
      const parsed = JSON.parse(raw) as { offer_context_id?: unknown }
      return typeof parsed.offer_context_id === 'string'
        ? parsed.offer_context_id
        : undefined
    } catch {
      return undefined
    }
  }

  const handlePurchase = async () => {
    if (
      !currentPkg
      || purchasing
      || restoringPurchases
      || applePurchaseInFlightRef.current
    ) return
    const existingAppleContext = loadPendingApplePaymentContext()
    if (isIOS() && existingAppleContext && existingAppleContext.ownerUserId === user?.userId) {
      showToast(t('member_payPendingRecovery'), 'error')
      return
    }
    if (
      !isIOS()
      && shouldBlockNewH5Checkout(
        localStorage.getItem('pendingOrderId'),
        localStorage.getItem('pendingPayChannel'),
      )
    ) {
      showToast(t('member_payPendingRecovery'), 'error')
      return
    }
    if (isIOS() && !currentPkg.purchase_available) {
      showToast(t('member_storeProductUnavailable'), 'error')
      return
    }
    track.orderClick(user?.isVip ? 'membership_renew' : 'membership_open', currentPkg.id)
    const pointsBefore = user?.points ?? 0
    setPurchasing(true)
    if (isIOS()) applePurchaseInFlightRef.current = true
    let orderId = ''
    let applePurchaseInvoked = false

    try {
      if (isIOS()) {
        const productContract = validateStoreProductContract({
          packageId: currentPkg.id,
          appleProductId: currentPkg.apple_product_id,
          purchaseMode: currentPkg.purchase_mode,
          isAutoRenew: currentPkg.is_auto_renew,
          storeProduct: currentPkg.store_product,
        })
        if (productContract.valid === false) {
          throw new Error(`Apple product contract mismatch: ${productContract.reason}`)
        }
        const order: CreateOrderResponse = await createOrder({
          packageId: currentPkg.id,
          payChannel: 'apple_iap',
          platform: 'ios',
          storefrontCountry: storefrontCountry || undefined,
          offerContextId: getOfferContextId(),
          idempotencyKey: randomUUID(),
        })
        orderId = order.order_id

        const appleProductId = order.apple_product_id
        if (!appleProductId) throw new Error('apple_product_id missing from order response')
        if (
          appleProductId !== currentPkg.apple_product_id
          || order.purchase_mode !== currentPkg.purchase_mode
        ) {
          throw new Error('Apple order product contract mismatch')
        }
        const appAccountToken = order.app_account_token
        if (!appAccountToken) throw new Error('app_account_token missing from order response')
        if (!user?.userId) throw new Error('authenticated consumer missing')
        savePendingApplePaymentContext({
          ownerUserId: user.userId,
          orderId,
          packageId: currentPkg.id,
          productId: appleProductId,
          appAccountToken,
          phase: 'order_created',
        })

        const bridge = window.__NATIVE_BRIDGE__
        if (!bridge) throw new Error('iOS Native bridge not available')
        savePendingApplePaymentContext({
          ownerUserId: user.userId,
          orderId,
          packageId: currentPkg.id,
          productId: appleProductId,
          appAccountToken,
          phase: 'storekit_started',
        })
        applePurchaseInvoked = true
        const storeResult = await bridge.purchase(
          appleProductId,
          appAccountToken,
          productContract.expectedProductType,
        )
        if (storeResult.status === 'cancelled') {
          await cancelAppleOrder(orderId)
          clearPendingApplePaymentContext(orderId)
          showToast(t('member_payCancelled'), 'success')
          return
        }
        if (storeResult.status === 'pending') {
          showToast(t('member_payPendingApple'), 'success')
          return
        }
        if (storeResult.status === 'unverified' || storeResult.status === 'error') {
          throw new Error(`Apple purchase outcome awaits recovery: ${storeResult.stage}`)
        }
        const receiptData = storeResult.receipt_data
        const nativeTransactionId = storeResult.transaction_id
        savePendingApplePaymentContext({
          ownerUserId: user.userId,
          orderId,
          packageId: currentPkg.id,
          productId: appleProductId,
          appAccountToken,
          transactionId: nativeTransactionId,
          phase: 'server_confirming',
        })

        const verifyResult = await verifyAppleIAP({ receipt_data: receiptData, order_id: orderId })
        if (verifyResult.confirmed) {
          try {
            await window.__NATIVE_BRIDGE__?.finishTransaction?.(
              nativeTransactionId || verifyResult.transaction_id,
            )
          } catch (err) {
            console.warn('[membership] transaction confirmed but finish is pending recovery', err)
          }
        }
        applyMembershipSnapshot(
          verifyResult.membership,
          verifyResult.points_balance_after,
        )
        clearPendingApplePaymentContext(orderId)

        try {
          const freshSub = verifyResult.membership ?? await getSubscription()
          setSubscription(freshSub)
        } catch { /* best-effort */ }

        const gifted = currentPkg.points_gift ?? Math.max(0, verifyResult.points_balance_after - pointsBefore)
        setPaySuccessData({ packageName: localize(currentPkg, 'name'), pointsGifted: gifted })
        track.paymentSuccess('apple_iap', currentPkg.id, currentPkg.price)

      } else {
        const platform = currentPayPlatform()
        const order: CreateOrderResponse = await createOrder({
          packageId: currentPkg.id,
          payChannel: 'hosted_checkout',
          platform,
          offerContextId: getOfferContextId(),
          idempotencyKey: randomUUID(),
        })
        orderId = order.order_id

        localStorage.setItem('pendingOrderId', orderId)
        localStorage.setItem('pendingPayChannel', 'hosted_checkout')
        if (user?.userId) bindPendingPaymentContextToUser(user.userId)
        if (!order.checkout_url) throw new Error('checkout_url missing from order response')
        if (platform === 'web') {
          window.location.assign(order.checkout_url)
          return
        }
        await Browser.open({ url: order.checkout_url })

        const outcome = await pollOrder(orderId)
        const decision = getH5PaymentRecoveryDecision(outcome)
        if (outcome === 'fulfillment_repairing') {
          showToast(t('member_payRepairing'), 'error')
          return
        }
        if (!decision.declareSuccess) {
          if (shouldClearPendingPaymentContext(
            outcome,
            localStorage.getItem('pendingPayChannel'),
          )) {
            clearPendingPaymentContext()
          }
          throw new Error('Payment not confirmed after polling')
        }

        // 拉取最新订阅刷新 VIP 状态
        try {
          const [sub, me] = await Promise.all([getSubscription(), getMe()])
          applyMembershipSnapshot(sub, me.points)
          setSubscription(sub)
        } catch { /* best-effort */ }

        clearPendingPaymentContext()

        setPaySuccessData({ packageName: localize(currentPkg, 'name'), pointsGifted: currentPkg.points_gift ?? 0 })
        track.paymentSuccess('hosted_checkout', currentPkg.id, currentPkg.price)
      }

    } catch (err) {
      console.error('[membership] payment failed', err)
      if (isIOS() && applePurchaseInvoked) {
        showToast(t('member_payConfirming'), 'success')
        if (orderId) {
          const outcome = await pollOrder(orderId)
          const decision = getH5PaymentRecoveryDecision(outcome)
          if (decision.declareSuccess) {
            try {
              const [sub, me] = await Promise.all([getSubscription(), getMe()])
              applyMembershipSnapshot(sub, me.points)
              setSubscription(sub)
            } catch { /* best-effort */ }
            clearPendingApplePaymentContext(orderId)
            showToast(t('member_paySuccessMsg'), 'success')
            track.paymentSuccess('poll_recovery', currentPkg.id, currentPkg.price)
          }
        }
        return
      }
      showToast(t('member_payFailed'), 'error')
      if (orderId) {
        const outcome = await pollOrder(orderId)
        const decision = getH5PaymentRecoveryDecision(outcome)
        if (decision.declareSuccess) {
          // 轮询发现已付款：刷新 VIP 状态再跳转
          try {
            const [sub, me] = await Promise.all([getSubscription(), getMe()])
            applyMembershipSnapshot(sub, me.points)
            setSubscription(sub)
          } catch { /* best-effort */ }
          clearPendingPaymentContext()
          showToast(t('member_paySuccessMsg'), 'success')
          track.paymentSuccess('poll_recovery', currentPkg.id, currentPkg.price)
          setTimeout(() => navigateAfterPay(), 1200)
        } else if (outcome === 'fulfillment_repairing') {
          showToast(t('member_payRepairing'), 'error')
        } else {
          if (shouldClearPendingPaymentContext(
            outcome,
            localStorage.getItem('pendingPayChannel'),
          )) {
              clearPendingPaymentContext()
          }
          showToast(t('member_payIncomplete'), 'error')
        }
      }
    } finally {
      applePurchaseInFlightRef.current = false
      setPurchasing(false)
    }
  }

  const handleManageSubscription = async () => {
    try {
      const management = await getSubscriptionManagement()
      if (!management.supported) {
        showToast(management.message, 'error')
        return
      }
      const bridge = window.__NATIVE_BRIDGE__
      if (Capacitor.isNativePlatform()) {
        if (!management.native_preferred || !bridge?.manageSubscriptions) {
          showToast(t('member_manageUnavailable'), 'error')
          return
        }
        const result = await bridge.manageSubscriptions()
        if (result.status !== 'presented') {
          showToast(t('member_manageUnavailable'), 'error')
          return
        }
        const [refreshed, me] = await Promise.all([getSubscription(), getMe()])
        applyMembershipSnapshot(refreshed, me.points)
        setSubscription(refreshed)
        return
      }
      const fallbackUrl = management.fallback_url ?? management.management_url
      if (!fallbackUrl) {
        showToast(management.message, 'error')
        return
      }
      window.location.assign(fallbackUrl)
    } catch (err) {
      console.error('[membership] open subscription management failed', err)
      showToast(t('member_payFailed'), 'error')
    }
  }

  const handleRestorePurchases = async (syncAppStore = false) => {
    if (!isIOS() || purchasing || restoringPurchases) return

    const bridge = window.__NATIVE_BRIDGE__
    setRestoreNeedsSync(false)
    setRestoringPurchases(true)
    try {
      const result = await restoreApplePurchases({
        bridge: bridge?.restorePurchases
          ? { restorePurchases: (shouldSync) => bridge.restorePurchases!(shouldSync) }
          : undefined,
        syncAppStore,
        recover: recoverAppleIAP,
        onTransactionError: () => {
          console.warn('[membership] one Apple entitlement could not be restored')
        },
      })

      let refreshFailed = false
      if (result.restored > 0) {
        try {
          const [refreshed, me] = await Promise.all([getSubscription(), getMe()])
          applyMembershipSnapshot(refreshed, me.points)
          setSubscription(refreshed)
        } catch {
          refreshFailed = true
          console.warn('[membership] restored purchases await entitlement refresh')
        }
      }

      const unresolved = result.failed + result.unverified + (refreshFailed ? 1 : 0)
      if (result.found === 0 && result.unverified === 0) {
        if (syncAppStore) {
          showToast(t('member_restoreNone'), 'error')
          setRestoreNeedsSync(true)
        } else {
          setRestoreNeedsSync(true)
        }
      } else if (result.restored > 0 && unresolved === 0) {
        showToast(t('member_restoreSuccess'), 'success')
      } else if (result.restored > 0) {
        showToast(t('member_restorePartial'), 'error')
      } else {
        showToast(t('member_restoreFailed'), 'error')
      }
    } catch (error) {
      console.warn('[membership] restore purchases failed', error)
      if (syncAppStore) setRestoreNeedsSync(true)
      showToast(t('member_restoreFailed'), 'error')
    } finally {
      setRestoringPurchases(false)
    }
  }

  const benefitColors = ['#FF6B9D', '#A855F7', '#EC4899', '#F59E0B', '#06B6D4'];

  if (!authInitialized) {
    return (
      <div className="min-h-screen font-nunito flex flex-col" style={{ background: '#F8F7FF', paddingBottom: 'env(safe-area-inset-bottom)' }}>
        <div className="flex-1 flex items-center justify-center">
          <div className="w-10 h-10 border-4 border-pink-200 border-t-pink-500 rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  // 未登录守卫
  if (!user) {
    return (
      <div className="min-h-screen font-nunito flex flex-col relative overflow-hidden"
        style={{ background: 'linear-gradient(160deg, #FFF0F6 0%, #F5EEFF 50%, #F0F4FF 100%)', paddingBottom: 'env(safe-area-inset-bottom)' }}>
        <div className="absolute top-0 right-0 w-56 h-56 rounded-full pointer-events-none"
          style={{ background: 'radial-gradient(circle, #FF6B9D22 0%, transparent 70%)', transform: 'translate(20%, -20%)' }} />
        <div className="absolute bottom-20 left-0 w-40 h-40 rounded-full pointer-events-none"
          style={{ background: 'radial-gradient(circle, #A855F718 0%, transparent 70%)', transform: 'translate(-30%, 0)' }} />
        <div className="max-w-md mx-auto w-full flex-1 flex flex-col items-center justify-center px-5">
          <div className="w-24 h-24 rounded-3xl flex items-center justify-center mb-6"
            style={{ background: 'linear-gradient(135deg, #FF6B9D 0%, #A855F7 100%)' }}>
            <i className="ri-vip-crown-2-fill text-white text-4xl"></i>
          </div>
          <h2 className="text-xl font-bold text-gray-800 mb-2">{t('member_loginTitle')}</h2>
          <p className="text-sm text-gray-400 mb-8 text-center leading-relaxed whitespace-pre-line">
            {t('member_loginDesc')}
          </p>
          <button
            onClick={() => navigate('/login?redirect=/membership')}
            className="w-full py-4 rounded-2xl text-white font-bold text-base cursor-pointer whitespace-nowrap"
            style={{ background: 'linear-gradient(135deg, #FF6B9D 0%, #A855F7 100%)' }}
          >
            {t('member_loginBtn')}
          </button>
        </div>
      </div>
    );
  }

  // ── VIP 已开通页 ─────────────────────────────────────────────────
  if (user?.isVip) {
    const expireTime = user.vipExpireTime ?? subscription?.expire_at ?? '';
    const isAutoRenewPackage = shouldShowAutoRenewStatus(subscription?.package_id);
    const isAutoRenew = isAutoRenewPackage
      && subscription?.is_auto_renew === true;

    const daysLeft = expireTime
      ? Math.max(0, Math.ceil((new Date(expireTime).getTime() - Date.now()) / 86400000))
      : null;

    return (
      <div className="min-h-screen flex flex-col font-nunito" style={{ background: '#F8F7FF', paddingBottom: 'env(safe-area-inset-bottom)' }}>
        <div className="max-w-md mx-auto w-full flex-1 flex flex-col">

          {/* ── 顶部渐变 Banner ── */}
          <div className="relative pb-6 px-5 overflow-hidden"
            style={{ background: 'linear-gradient(160deg, #FF6B9D 0%, #A855F7 100%)', paddingTop: 'calc(env(safe-area-inset-top) + 1rem)' }}>
            <div className="absolute top-0 right-0 w-56 h-56 rounded-full opacity-10 bg-white"
              style={{ transform: 'translate(30%, -30%)' }} />
            <div className="absolute bottom-0 left-0 w-36 h-36 rounded-full opacity-10 bg-white"
              style={{ transform: 'translate(-30%, 40%)' }} />

            <div className="flex items-center mb-5">
              <button onClick={() => navigate(-1)}
                className="w-8 h-8 flex items-center justify-center cursor-pointer rounded-full bg-white/20">
                <i className="ri-arrow-left-line text-white text-lg"></i>
              </button>
            </div>

            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0"
                style={{ background: 'rgba(255,255,255,0.2)' }}>
                <i className="ri-vip-crown-2-fill text-white text-2xl"></i>
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <h1 className="text-lg font-bold text-white">{t('member_vipStatus')}</h1>
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-400/90 text-white">{t('member_vipActive')}</span>
                </div>
                {expireTime && (
                  <p className="text-white/70 text-xs">{t('member_expireAt')}{expireTime.slice(0, 10)}</p>
                )}
              </div>
            </div>

            {/* 3格状态区 */}
            <div className="flex gap-2 mt-4">
              <div className="flex-1 flex items-center gap-2 bg-white/15 rounded-xl px-3 py-2">
                <i className="ri-flashlight-fill text-amber-300 text-sm"></i>
                <div>
                  <p className="text-white font-bold text-sm leading-none" data-testid="vip-points-balance">{user.points}</p>
                  <p className="text-white/60 text-[10px] mt-0.5">{t('member_remainPoints')}</p>
                </div>
              </div>
              <div className="flex-1 flex items-center gap-2 bg-white/15 rounded-xl px-3 py-2">
                <i className="ri-calendar-check-line text-amber-300 text-sm"></i>
                <div>
                  <p className="text-white font-bold text-sm leading-none">{daysLeft !== null ? `${daysLeft}${t('member_daysUnit')}` : '--'}</p>
                  <p className="text-white/60 text-[10px] mt-0.5">{t('member_remainDays')}</p>
                </div>
              </div>
              {isAutoRenewPackage && (
                <div className="flex-1 flex items-center gap-2 bg-white/15 rounded-xl px-3 py-2">
                  <i className={`${isAutoRenew ? 'ri-refresh-line' : 'ri-close-circle-line'} text-amber-300 text-sm`}></i>
                  <div>
                    <p className="text-white font-bold text-sm leading-none">{isAutoRenew ? t('member_autoRenew') : t('member_cancelledRenew')}</p>
                    <p className="text-white/60 text-[10px] mt-0.5">{isAutoRenew ? t('member_renewing') : t('member_notRenewing')}</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* ── 所选套餐权益预览 ── */}
          <div className="px-5 pt-5 pb-2" data-testid="selected-plan-benefits">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs font-bold text-gray-400 tracking-wider">{t('member_selectedPlanBenefits')}</p>
              {currentPkg && (
                <span className="text-xs font-semibold" style={{ color: accentColor }} data-testid="selected-plan-name">
                  {localize(currentPkg, 'name')}
                </span>
              )}
            </div>
            {currentPkg && (
              <div className="bg-white rounded-2xl px-4 py-3 mb-2.5 flex items-center gap-3"
                style={{ border: `1.5px solid ${accentColor}18` }}>
                <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ background: `${accentColor}12` }}>
                  <i className="ri-flashlight-fill text-sm" style={{ color: accentColor }}></i>
                </div>
                <p className="text-gray-800 text-xs font-semibold" data-testid="selected-plan-points">
                  {t(
                    currentPkg.purchase_mode === 'auto_renewable'
                      ? 'member_firstPurchaseGift'
                      : 'member_purchaseGift',
                    { points: currentPkg.points_gift },
                  )}
                </p>
              </div>
            )}
            <div className="space-y-2.5">
              {(currentPkg?.benefits ?? []).map((b, i) => {
                const bColor = benefitColors[i % benefitColors.length];
                return (
                  <div key={i} className="bg-white rounded-2xl px-4 py-3"
                    style={{ border: `1.5px solid ${bColor}18` }}>
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                        style={{ background: `${bColor}12` }}>
                        <i className={`${b.icon} text-sm`} style={{ color: bColor }}></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-gray-800 text-xs font-semibold leading-tight">{localize(b, 'title')}</p>
                        <p className="text-gray-400 text-[10px] leading-tight">{localize(b, 'desc')}</p>
                      </div>
                      <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0"
                        style={{ background: currentPkg?.gradient ?? gradient }}>
                        <i className="ri-check-line text-white text-xs"></i>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* ── 续费套餐横向滑动 ── */}
          <div className="pb-2 pt-3">
            <p className="text-xs font-bold text-gray-400 mb-3 px-5 tracking-wider">{t('member_renewPlan')}</p>
            {packagesLoading && displayPackages.length === 0 ? (
              <PackageCardsSkeleton />
            ) : packagesError && displayPackages.length === 0 ? (
              <PackageLoadError
                message={t('member_packagesLoadFailed')}
                accentColor={accentColor}
                onRetry={() => setLoadAttempt((attempt) => attempt + 1)}
              />
            ) : (
              <>
                <div ref={scrollRef} className="flex gap-3 overflow-x-auto px-5 pb-2 pt-3"
                  style={{ scrollbarWidth: 'none' }}>
                  {displayPackages.map((pkg) => (
                    <PkgCard
                      key={pkg.id}
                      pkg={pkg}
                      selected={effectivePkgId === pkg.id}
                      priceLoading={storeProductsLoading && pkg.store_price_required}
                      onSelect={() => setSelectedPkgId(pkg.id)}
                    />
                  ))}
                  <div className="flex-shrink-0 w-1"></div>
                </div>
                <div className="flex justify-center gap-1.5 mt-2">
                  {displayPackages.map((pkg) => (
                    <div key={pkg.id} onClick={() => setSelectedPkgId(pkg.id)}
                      className="rounded-full transition-all cursor-pointer"
                      style={{
                        width: effectivePkgId === pkg.id ? 16 : 5, height: 5,
                        background: effectivePkgId === pkg.id ? pkg.gradient : '#E5E7EB',
                      }} />
                  ))}
                </div>
              </>
            )}
            {storeProductsError && (
              <div className="mt-3">
                <PackageLoadError
                  message={t('member_storePriceFailed')}
                  accentColor={accentColor}
                  onRetry={() => setLoadAttempt((attempt) => attempt + 1)}
                />
              </div>
            )}
          </div>

          {/* ── 底部按钮 ── */}
          <div className="px-5 mt-auto pb-8 pt-3 space-y-3">
            {currentPkg?.purchase_mode === 'consumable' ? (
              <>
                <button
                  onClick={handlePurchase}
                  disabled={purchasing || restoringPurchases || (isIOS() && !currentPkg.purchase_available)}
                  className="w-full h-14 rounded-2xl text-white font-bold text-base cursor-pointer whitespace-nowrap disabled:opacity-60 active:scale-95 transition-transform"
                  style={{ background: gradient, boxShadow: `0 4px 18px ${accentColor}40` }}
                >
                  {purchasing
                    ? t('member_processing')
                    : storeProductsLoading && isIOS()
                      ? t('member_storePriceLoading')
                      : `${t('member_openNow')} · ${getDisplayPrice(currentPkg)}`}
                </button>
                {isIOS() && !storeProductsLoading && !currentPkg.purchase_available && (
                  <p className="text-center text-xs text-red-400" data-testid="store-product-error">
                    {t('member_storeProductUnavailable')}
                  </p>
                )}
              </>
            ) : (
              <p className="text-center text-sm text-gray-400 py-3">
                {t('member_repurchaseAfterExpiry', { defaultValue: '当前会员到期后可重新购买' })}
              </p>
            )}
            {isAutoRenewPackage && (
              <button
                onClick={() => { void handleManageSubscription() }}
                className="w-full h-14 rounded-2xl text-white font-bold text-base cursor-pointer whitespace-nowrap active:scale-95 transition-transform"
                style={{ background: gradient, boxShadow: `0 4px 18px ${accentColor}40` }}
              >
                <i className="ri-external-link-line mr-1"></i>
                {t('member_manageSubscription', { defaultValue: '管理自动续费' })}
              </button>
            )}
            {isIOS() && (
              <RestorePurchasesButton
                disabled={purchasing || restoringPurchases}
                restoring={restoringPurchases}
                needsSync={restoreNeedsSync}
                accentColor={accentColor}
                onRestore={() => { void handleRestorePurchases(false) }}
                onSync={() => { void handleRestorePurchases(true) }}
              />
            )}
          </div>
        </div>

        {/* Toast */}
        {toast && (
          <div
            className="fixed bottom-24 left-1/2 -translate-x-1/2 z-50 px-5 py-3 rounded-2xl text-white text-sm font-medium shadow-lg"
            style={{ background: toast.type === 'success' ? '#22C55E' : '#EF4444' }}
          >
            {toast.text}
          </div>
        )}

        {/* 续费成功弹窗 */}
        {paySuccessData && (
          <div className="fixed inset-0 z-50 flex items-center justify-center px-5 bg-black/40">
            <div className="w-full max-w-sm bg-white rounded-3xl p-6 animate-scale-in text-center">
              <div className="w-20 h-20 rounded-3xl mx-auto mb-4 flex items-center justify-center"
                style={{ background: gradient }}>
                <i className="ri-vip-crown-fill text-white text-4xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-1">{t('member_successTitle')}</h3>
              <p className="text-sm text-gray-400 mb-1">
                {t('member_successPlan')} <span className="font-semibold" style={{ color: accentColor }}>{paySuccessData.packageName}</span> {t('member_successPlanSuffix')}
              </p>
              {paySuccessData.pointsGifted > 0 && (
                <p className="text-sm text-gray-400 mb-5">
                  {t('member_successPoints')} <span className="text-amber-500 font-semibold">{paySuccessData.pointsGifted}</span> {t('member_successPointsSuffix')}
                </p>
              )}
              <button
                onClick={() => { setPaySuccessData(null); navigateAfterPay(); }}
                className="w-full h-12 rounded-2xl text-white font-bold text-sm cursor-pointer whitespace-nowrap"
                style={{ background: gradient }}
              >
                {t('member_startCreate')}
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── 未开通页 ──────────────────────────────────────────────────────
  return (
    <div className="min-h-screen flex flex-col font-nunito" style={{ background: '#F8F7FF', paddingBottom: 'env(safe-area-inset-bottom)' }}>
      <div className="max-w-md mx-auto w-full flex-1 flex flex-col">

        {/* ── 顶部渐变 Banner ── */}
        <div className="relative pb-8 px-5 overflow-hidden"
          style={{ background: 'linear-gradient(160deg, #FF6B9D 0%, #A855F7 100%)', paddingTop: 'calc(env(safe-area-inset-top) + 1rem)' }}>
          <div className="absolute top-0 right-0 w-48 h-48 rounded-full opacity-10 bg-white"
            style={{ transform: 'translate(30%, -30%)' }} />
          <div className="absolute bottom-0 left-0 w-32 h-32 rounded-full opacity-10 bg-white"
            style={{ transform: 'translate(-30%, 30%)' }} />

          <div className="flex items-center gap-3 mb-4">
            <button
              onClick={() => navigate(-1)}
              className="w-8 h-8 flex items-center justify-center cursor-pointer rounded-full bg-white/20 flex-shrink-0"
            >
              <i className="ri-arrow-left-line text-white text-lg"></i>
            </button>
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-lg bg-white/20 flex items-center justify-center">
                <i className="ri-vip-crown-2-fill text-white text-xs"></i>
              </div>
              <span className="text-white/80 text-sm font-medium">{t('member_title')}</span>
            </div>
          </div>
          <h1 className="text-2xl font-bold text-white mb-1">{t('member_subtitle')}</h1>
          <p className="text-white/60 text-sm">{t('member_headerDesc')}</p>
          {user && (
            <div className="mt-3 inline-flex items-center gap-1.5 bg-white/20 rounded-full px-3 py-1.5">
              <i className="ri-flashlight-fill text-amber-300 text-xs"></i>
              <span className="text-white text-xs font-medium">{t('member_currentPoints')}{user.points}</span>
            </div>
          )}
        </div>

        {/* ── 积分与 VIP 关系说明 ── */}
        <div className="px-5 pt-5">
          <div className="bg-white rounded-3xl p-4" style={{ border: `1.5px solid ${accentColor}18` }}>
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: `${accentColor}12` }}>
                <i className="ri-question-answer-line text-base" style={{ color: accentColor }}></i>
              </div>
              <div>
                <p className="text-sm font-bold text-gray-800">{t('member_valueTitle')}</p>
                <p className="text-[11px] text-gray-400 mt-0.5">{t('member_valueDesc')}</p>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {[
                ['ri-gift-line', t('member_valueFreeTitle'), t('member_valueFreeDesc')],
                ['ri-flashlight-line', t('member_valueCreditsTitle'), t('member_valueCreditsDesc')],
                ['ri-vip-crown-2-line', t('member_valueVipTitle'), t('member_valueVipDesc')],
              ].map(([itemIcon, title, desc]) => (
                <div key={title} className="rounded-2xl p-2.5" style={{ background: `${accentColor}08` }}>
                  <i className={`${itemIcon} text-base`} style={{ color: accentColor }}></i>
                  <p className="text-[11px] font-bold text-gray-800 mt-1 leading-tight">{title}</p>
                  <p className="text-[10px] text-gray-400 mt-1 leading-snug">{desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── 套餐横向滑动卡片 ── */}
        <div className="pt-5 pb-2">
          <p className="text-xs font-bold text-gray-400 mb-3 px-5 tracking-wider">{t('member_selectPlan')}</p>
          {packagesLoading && displayPackages.length === 0 ? (
            <PackageCardsSkeleton />
          ) : packagesError && displayPackages.length === 0 ? (
            <PackageLoadError
              message={t('member_packagesLoadFailed')}
              accentColor={accentColor}
              onRetry={() => setLoadAttempt((attempt) => attempt + 1)}
            />
          ) : (
            <>
              <div className="flex gap-3 overflow-x-auto px-5 pb-3 pt-3" style={{ scrollbarWidth: 'none' }}>
                {displayPackages.map((pkg) => (
                  <PkgCard
                    key={pkg.id}
                    pkg={pkg}
                    selected={effectivePkgId === pkg.id}
                    priceLoading={storeProductsLoading && pkg.store_price_required}
                    onSelect={() => setSelectedPkgId(pkg.id)}
                  />
                ))}
                <div className="flex-shrink-0 w-1"></div>
              </div>
              <div className="flex justify-center gap-1.5 mt-1">
                {displayPackages.map((pkg) => (
                  <div key={pkg.id} onClick={() => setSelectedPkgId(pkg.id)}
                    className="rounded-full transition-all cursor-pointer"
                    style={{
                      width: effectivePkgId === pkg.id ? 16 : 5,
                      height: 5,
                      background: effectivePkgId === pkg.id ? pkg.gradient : '#E5E7EB',
                    }} />
                ))}
              </div>
            </>
          )}
          {storeProductsError && (
            <div className="mt-3">
              <PackageLoadError
                message={t('member_storePriceFailed')}
                accentColor={accentColor}
                onRetry={() => setLoadAttempt((attempt) => attempt + 1)}
              />
            </div>
          )}
        </div>

        {/* ── 套餐详情 + 专属权益合并卡片 ── */}
        {currentPkg && (
          <div className="px-5 pt-4 pb-3">
            <div className="bg-white rounded-3xl overflow-hidden"
              style={{ border: `1.5px solid ${accentColor}20` }}>

              <div className="flex items-center justify-between px-4 py-3"
                style={{ background: `linear-gradient(135deg, ${accentColor}10, ${accentColor}05)`, borderBottom: `1px solid ${accentColor}15` }}>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-gray-800 font-bold text-sm">{localize(currentPkg, 'name')}</span>
                  {currentPkg.tag && (
                    <span className="text-[10px] font-bold text-white px-2 py-0.5 rounded-full"
                      style={{ background: gradient }}>
                      {localize(currentPkg, 'tag')}
                    </span>
                  )}
                  {getDailyPrice(currentPkg, t) && (
                    <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full"
                      style={{ background: `${accentColor}15`, color: accentColor }}>
                      {getDailyPrice(currentPkg, t)}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <div className="flex items-center gap-0.5">
                    <i className="ri-flashlight-fill text-amber-400 text-xs"></i>
                    <span className="text-amber-500 text-xs font-bold">+{currentPkg.points_gift}</span>
                  </div>
                  <div className="flex items-baseline gap-0.5">
                    {storeProductsLoading && currentPkg.store_price_required ? (
                      <span className="inline-block h-6 w-16 rounded-md bg-gray-100 animate-pulse" data-testid="store-price-detail-skeleton" />
                    ) : (
                      <>
                        <span className="text-xl font-bold" style={{ color: accentColor }}>{getDisplayPrice(currentPkg)}</span>
                        <span className="text-gray-400 text-xs">{priceUnit}</span>
                      </>
                    )}
                  </div>
                </div>
              </div>

              <div className="px-4 py-4">
                <p className="text-xs font-bold text-gray-400 mb-3 tracking-wider">{t('member_exclusiveBenefits')}</p>
                <div className="space-y-3">
                  {currentPkg.benefits.map((b, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                        style={{ background: `${accentColor}12` }}>
                        <i className={`${b.icon} text-sm`} style={{ color: accentColor }}></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-gray-800 text-xs font-semibold">{localize(b, 'title')}</p>
                        <p className="text-gray-400 text-[11px] mt-0.5">{localize(b, 'desc')}</p>
                      </div>
                      <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0"
                        style={{ background: gradient }}>
                        <i className="ri-check-line text-white text-xs"></i>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── 底部开通按钮 ── */}
        <div className="px-5 mt-auto pb-6">
          <button
            onClick={handlePurchase}
            disabled={purchasing || restoringPurchases || (isIOS() && !currentPkg?.purchase_available)}
            className="w-full h-14 rounded-2xl text-white font-bold text-base cursor-pointer whitespace-nowrap disabled:opacity-60 active:scale-95 transition-transform mb-3"
            style={{ background: gradient, boxShadow: `0 4px 20px ${accentColor}40` }}
          >
            {purchasing
              ? t('member_processing')
              : storeProductsLoading && isIOS()
                ? t('member_storePriceLoading')
                : currentPkg && isIOS() && !currentPkg.purchase_available
                ? t('member_storeProductUnavailable')
                : `${t('member_openNow')} · ${getDisplayPrice(currentPkg)}${priceUnit}`}
          </button>
          {isIOS() && !storeProductsLoading && currentPkg && !currentPkg.purchase_available && (
            <p className="text-center text-xs text-red-400 mb-3" data-testid="store-product-error">
              {t('member_storeProductUnavailable')}
            </p>
          )}
          {isIOS() && (
            <div className="mb-3">
              <RestorePurchasesButton
                disabled={purchasing || restoringPurchases}
                restoring={restoringPurchases}
                needsSync={restoreNeedsSync}
                accentColor={accentColor}
                onRestore={() => { void handleRestorePurchases(false) }}
                onSync={() => { void handleRestorePurchases(true) }}
              />
            </div>
          )}
          <p className="text-center text-xs text-gray-400">
            {t('member_agreePrefix')}
            <span className="cursor-pointer font-medium" style={{ color: accentColor }}
              onClick={() => setActiveModal('memberAgreement')}> {t('member_memberAgreement')}</span>
            {currentPkg?.id === 'monthly_auto'
              && currentPkg.purchase_mode === 'auto_renewable' && (
              <>
                {' '}{t('member_and')}{' '}
                <span className="cursor-pointer font-medium" style={{ color: accentColor }}
                  onClick={() => setActiveModal('autoRenewAgreement')}>{t('member_autoRenewAgreement')}</span>
              </>
            )}
          </p>
        </div>
      </div>

      {/* Toast */}
      {toast && (
        <div
          className="fixed bottom-24 left-1/2 -translate-x-1/2 z-50 px-5 py-3 rounded-2xl text-white text-sm font-medium shadow-lg"
          style={{ background: toast.type === 'success' ? '#22C55E' : '#EF4444' }}
        >
          {toast.text}
        </div>
      )}

      {/* 开通成功弹窗 */}
      {paySuccessData && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-5 bg-black/40">
          <div className="w-full max-w-sm bg-white rounded-3xl p-6 animate-scale-in text-center">
            <div className="w-20 h-20 rounded-3xl mx-auto mb-4 flex items-center justify-center"
              style={{ background: gradient }}>
              <i className="ri-vip-crown-fill text-white text-4xl"></i>
            </div>
            <h3 className="text-xl font-bold text-gray-800 mb-1">{t('member_successTitle')}</h3>
            <p className="text-sm text-gray-400 mb-1">
              {t('member_successPlan')} <span className="font-semibold" style={{ color: accentColor }}>{paySuccessData.packageName}</span> {t('member_successPlanSuffix')}
            </p>
            {paySuccessData.pointsGifted > 0 && (
              <p className="text-sm text-gray-400 mb-5">
                {t('member_successPoints')} <span className="text-amber-500 font-semibold">{paySuccessData.pointsGifted}</span> {t('member_successPointsSuffix')}
              </p>
            )}
            {currentPkg && (
              <div className="grid grid-cols-2 gap-2.5 mb-5 text-left">
                {currentPkg.benefits.map((b, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <i className="ri-checkbox-circle-fill text-sm flex-shrink-0" style={{ color: accentColor }}></i>
                    <span className="text-xs text-gray-600">{localize(b, 'title')}</span>
                  </div>
                ))}
              </div>
            )}
            <button
              onClick={() => { setPaySuccessData(null); navigateAfterPay(); }}
              className="w-full h-12 rounded-2xl text-white font-bold text-sm cursor-pointer whitespace-nowrap"
              style={{ background: gradient }}
            >
              {t('member_startCreate')}
            </button>
          </div>
        </div>
      )}

      {activeModal === 'memberAgreement' && (
        <AgreementSheet
          agreement={config?.agreements?.member_service}
          onClose={() => setActiveModal(null)}
          confirmLabel={t('member_agreeBtn')}
        />
      )}

      {activeModal === 'autoRenewAgreement' && (
        <AgreementSheet
          agreement={config?.agreements?.auto_renew}
          onClose={() => setActiveModal(null)}
          confirmLabel={t('member_agreeBtn')}
        />
      )}
    </div>
  );
}
